import React, { useCallback, useMemo } from "react";
import { Card } from "../../../components/card";
import { useAuthSession } from "../../auth/shared";
import { HomeConditionalLayout } from "../../../components/home/HomeConditionalLayout";
import DecorativeIcon from "../../../components/icons/DecorativeIcon";
import { useDashboardAnalytics } from "../hooks/useDashboardAnalytics";
import { useDashboardOwnCreated } from "../hooks/useDashboardOwnCreated";
import { useDashboardStatistics } from "../hooks/useDashboardStatistics";
import { DashboardAnalyticsSection } from "../components/DashboardAnalyticsSection";
import { DashboardStatsSection } from "../components/DashboardStatsSection";

const getFormattedDate = (): string => {
  const now = new Date();
  const dayOfWeek = now.toLocaleDateString("en-US", { weekday: "long" });
  const month = now.toLocaleDateString("en-US", { month: "long" });
  const day = now.getDate();

  const getOrdinalSuffix = (dayOfMonth: number): string => {
    if (dayOfMonth > 3 && dayOfMonth < 21) return "th";
    switch (dayOfMonth % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${dayOfWeek}, ${month} ${day}${getOrdinalSuffix(day)}`;
};

function DashboardPage() {
  const statistics = useDashboardStatistics();
  const analytics = useDashboardAnalytics();
  const ownCreated = useDashboardOwnCreated();
  const { user } = useAuthSession();

  const { retry: retryStatistics } = statistics;
  const { retryGrowth, retryActivity } = analytics;
  const { retry: retryOwnCreated } = ownCreated;

  const isRefreshingAll = useMemo(
    () =>
      statistics.refreshing ||
      analytics.growthRefreshing ||
      analytics.activityRefreshing ||
      ownCreated.refreshing,
    [
      statistics.refreshing,
      analytics.growthRefreshing,
      analytics.activityRefreshing,
      ownCreated.refreshing,
    ],
  );

  const refreshAll = useCallback(async () => {
    await Promise.allSettled([
      retryStatistics(),
      retryGrowth(),
      retryActivity(),
      retryOwnCreated(),
    ]);
  }, [retryStatistics, retryGrowth, retryActivity, retryOwnCreated]);

  const currentDate = getFormattedDate();
  const userName = user ? `${user.first_name} ${user.last_name}` : "User";

  return (
    <Card className="p-6 border-none rounded-none dark:bg-[#394150] bg-[#EEF9EF] border-gray-200 dark:border-gray-700 relative overflow-hidden">
      <div className="absolute top-0 right-0 pointer-events-none">
        <DecorativeIcon className="w-[261px] h-[180px] opacity-100" />
      </div>

      <DashboardStatsSection
        title={`Welcome, ${userName}.`}
        subtitle={currentDate}
        values={statistics.statValues}
        hasData={statistics.hasData}
        loading={statistics.loading}
        refreshing={statistics.refreshing || isRefreshingAll}
        error={statistics.error}
        onRetry={statistics.retry}
        onRefreshAll={refreshAll}
      />

      <DashboardAnalyticsSection
        timeFilter={analytics.timeFilter}
        onTimeFilterChange={analytics.setTimeFilter}
        trend={analytics.trend}
        userGrowthChartData={analytics.growthChartPoints}
        growthLoading={analytics.growthLoading}
        growthError={analytics.growthError}
        onRetryGrowth={analytics.retryGrowth}
        activityDonutData={analytics.activityDonutItems}
        activityLoading={analytics.activityLoading}
        activityError={analytics.activityError}
        onRetryActivity={analytics.retryActivity}
      />

      <HomeConditionalLayout
        ownCreated={ownCreated.ownCreatedData}
        isLoading={ownCreated.loading}
        error={ownCreated.error}
        onRetry={ownCreated.retry}
      />
    </Card>
  );
}

export default DashboardPage;
